import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DynamicTabBar, Tab } from "@/components/ui/DynamicTabBar";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Users, Briefcase, MessageSquare, History, FileText, Drama, Mail, CheckSquare2 } from "lucide-react";
import { useCompanyMatchesList } from "@/hooks/companies/useCompanyMatchesList";
import { useCompanyJobsList } from "@/hooks/companies/useCompanyJobsList";
import { useCompanyContactsList } from "@/hooks/companies/useCompanyContactsList";
import { useCompanyPositions } from "@/hooks/companies/useCompanyPositions";
import { useCompanyDetail, Company } from "@/hooks/companies/useCompanyDetail";
import { AddContactForm } from "@/components/companies/AddContactForm";
import { CompanyComments } from "@/components/companies/CompanyComments";
import { CompanyMatchesTab } from "@/components/companies/CompanyMatchesTab";
import { CompanyOffersTab } from "@/components/companies/CompanyOffersTab";
import { CompanyContactsTab } from "@/components/companies/CompanyContactsTab";
import { CompanyPositionsTab } from "@/components/companies/CompanyPositionsTab";
import { CompanyDetailSkeleton } from "@/components/companies/CompanyDetailSkeleton";
import { CompanyNotFound } from "@/components/companies/CompanyNotFound";
import { CompanyHistoryTab } from "@/components/companies/CompanyHistoryTab";
import CompanyDocumentsTab from "@/components/companies/CompanyDocumentsTab";
import { useCompanies } from "@/hooks/useCompanies";
import { useOrganizationManagement } from "@/hooks/useOrganizationManagement";
import { useTranslation } from "react-i18next";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { fetchCustomFields, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useAuth } from "@/providers/AuthProvider";
import { CompanyDetailHeader } from "@/components/companies/CompanyDetailHeader";
import { CompanyProfile } from "@/components/companies/CompanyProfile";
import { CompanyTasksTab } from "@/components/companies/CompanyTasksTab";
import { toast } from "@/hooks/use-toast";
import { EntityMailbox } from "@/components/inbox/EntityMailbox";
import { useTrackPageVisit } from "@/hooks/useEntityHistory";

interface Job {
  id: string;
  title: string;
  company_id: string;
  company_name?: string;
  status?: string;
  created_at: string;
  skills?: string[];
  description?: string;
  [key: string]: any;
}

const CompanyDetail = () => {
	const { id } = useParams<{ id: string }>();
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [addContactModalOpen, setAddContactModalOpen] = useState(false);
	const [activeTab, setActiveTab] = useState("offers");
	const [isEditing, setIsEditing] = useState(false);
	const [editCompany, setEditCompany] = useState<Company | null>(null);
	const [customFields, setCustomFields] = useState([]);
	const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
	const [customFieldsError, setCustomFieldsError] = useState<string | null>(null);
	const { currentOrganization, user } = useAuth();
	const organizationId = currentOrganization?.id;
	
	const { company, loading, refreshCompany } = useCompanyDetail(id);

	usePageTitle(company ? company.name : "Company");

	// Track page visit for history
	useTrackPageVisit('company', company?.id, company?.name);

	const companyTabConfig: Tab[] = useMemo(() => [
		{ id: "offers", label: t("companies.detail.tabs.offers"), icon: <Briefcase className="h-4 w-4" /> },
		{ id: "matches", label: t("companies.detail.tabs.matches"), icon: <Users className="h-4 w-4" /> },
		{ id: "contacts", label: t("companies.detail.tabs.contacts"), icon: <Users className="h-4 w-4" /> },
		{ id: "positions", label: t("companies.detail.tabs.positions"), icon: <Drama className="h-4 w-4" /> },
		{ id: "documents", label: t("companies.detail.tabs.documents"), icon: <FileText className="h-4 w-4" /> },
		{ id: "tasks", label: t("companies.tabs.tasks", "Tasks"), icon: <CheckSquare2 className="h-4 w-4" /> },
		{ id: "comments", label: t("companies.detail.tabs.comments"), icon: <MessageSquare className="h-4 w-4" /> },
		{ id: "mailbox", label: "Mailbox", icon: <Mail className="h-4 w-4" /> },
		{ id: "history", label: t("companies.detail.tabs.history"), icon: <History className="h-4 w-4" /> },
	], [t]);

	// Switch to tasks tab if task parameter is present
	useEffect(() => {
		const taskId = searchParams.get('task');
		if (taskId && activeTab !== 'tasks') {
			setActiveTab('tasks');
		}
	}, [searchParams]);

	const { fetchCompanies } = useCompanies();
	const { handleEditCompany, handleDeleteCompany } = useOrganizationManagement({ fetchCompanies });
  
  const { 
    matches, 
    matchesLoading, 
    searchTerm: matchesSearchTerm, 
    setSearchTerm: setMatchesSearchTerm,
    viewMode: matchesViewMode,
    setViewMode: setMatchesViewMode
  } = useCompanyMatchesList(id);
  
  const {
    jobs,
    jobsLoading,
    searchTerm: jobsSearchTerm,
    setSearchTerm: setJobsSearchTerm,
    viewMode: jobsViewMode,
    setViewMode: setJobsViewMode
  } = useCompanyJobsList(id);
  
  const {
    contacts,
    contactsLoading,
    searchTerm: contactsSearchTerm,
    setSearchTerm: setContactsSearchTerm,
    viewMode: contactsViewMode,
    setViewMode: setContactsViewMode,
    refreshContacts
  } = useCompanyContactsList(id);
  
  const {
    positions,
    loading: positionsLoading,
    error: positionsError,
    searchTerm: positionsSearchTerm,
    setSearchTerm: setPositionsSearchTerm,
    viewMode: positionsViewMode,
    setViewMode: setPositionsViewMode,
    refreshPositions
  } = useCompanyPositions(id || '', company?.name || '');
  
  useEffect(() => {
    if (!organizationId) return;
    setCustomFieldsLoading(true);
    setCustomFieldsError(null);
    fetchCustomFields(organizationId)
      .then((data: CustomFieldsJSON) => {
        setCustomFields(data.Companies || []);
      })
      .catch(() => {
        setCustomFieldsError("Failed to load custom fields");
      })
      .finally(() => setCustomFieldsLoading(false));
  }, [organizationId]);
  
  if (loading) {
    return <CompanyDetailSkeleton />;
  }
  
  if (!company) {
    return <CompanyNotFound />;
  }
  
  const handleAddContact = async (contactData: any) => {
    setAddContactModalOpen(false);
    refreshContacts();
  };

  const handleEdit = () => {
    setEditCompany(company);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditCompany(company);
  };

  const handleSave = async () => {
    if (!editCompany || !company) return;
    
    // Only save if there are actual changes
    const hasChanges = JSON.stringify(editCompany) !== JSON.stringify(company);
    if (!hasChanges) {
      setIsEditing(false);
      toast({ title: "No changes", description: "No changes to save." });
      return;
    }
    
    // Immediately close edit mode and show success toast for better UX
    setIsEditing(false);
    toast({ title: "Success", description: "Company updated successfully" });
    
    try {
      // Extract custom fields from the editCompany
      const { custom_fields, ...regularFields } = editCompany as any;
      
      // Call the update function with regular fields and custom fields separately
      const success = await handleEditCompany(regularFields, custom_fields || {});
      if (success) {
        refreshCompany();
      } else {
        // If save failed, revert to edit mode
        setIsEditing(true);
        toast({ title: "Error", description: "Failed to update company", variant: "destructive" });
      }
    } catch (error) {
      // If save failed, revert to edit mode
      setIsEditing(true);
      toast({ title: "Error", description: "Failed to update company", variant: "destructive" });
    }
  };

  const handleDeleteClick = async () => {
    if (!id) return;
    
    const success = await handleDeleteCompany(id);
    if (success)
      navigate('/companies');
  };
  
  return (
    <div>
      <CompanyDetailHeader
        isEditing={isEditing}
        onEdit={handleEdit}
        onSave={handleSave}
        onCancel={handleCancel}
        handleDelete={handleDeleteClick}
        companyName={company?.name || ''}
        companyId={company?.id || ''}
      />
      
      {/* Ensure no space above CompanyProfile sticky banner */}
      <div className="p-0 m-0">
        <CompanyProfile
          company={isEditing ? editCompany! : company}
          isEditing={isEditing}
          onChange={setEditCompany}
          onSave={handleSave}
          onCancel={handleCancel}
          refreshCompany={refreshCompany}
        />
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <DynamicTabBar
          tabs={companyTabConfig}
          pinnedIds={["offers", "matches", "contacts"]}
          dynamicSlotCount={3}
          defaultDynamicIds={["positions", "documents", "comments"]}
          activeTabId={activeTab}
          onTabChange={setActiveTab}
          entityType="company"
          userId={user?.id || ""}
        />
        
        <TabsContent value="matches" className="space-y-4">
          <CompanyMatchesTab
            matches={matches}
            loading={matchesLoading}
            searchTerm={matchesSearchTerm}
            setSearchTerm={setMatchesSearchTerm}
            viewMode={matchesViewMode}
            setViewMode={(mode: 'list' | 'card') => setMatchesViewMode(mode)}
          />
        </TabsContent>
        
        <TabsContent value="offers" className="space-y-4">
          <CompanyOffersTab
            jobs={jobs as Job[]}
            loading={jobsLoading}
            searchTerm={jobsSearchTerm}
            setSearchTerm={setJobsSearchTerm}
            viewMode={jobsViewMode}
            setViewMode={setJobsViewMode as (mode: any) => void}
            companyId={id || ''}
          />
        </TabsContent>
          <TabsContent value="contacts" className="space-y-4">
          <CompanyContactsTab
            contacts={contacts}
            loading={contactsLoading}
            searchTerm={contactsSearchTerm}
            setSearchTerm={setContactsSearchTerm}
            viewMode={contactsViewMode}
            setViewMode={setContactsViewMode as (mode: any) => void}
            companyId={id || ''}
            onAddContact={() => setAddContactModalOpen(true)}
            refreshContacts={refreshContacts}
          />
        </TabsContent>
        
        <TabsContent value="positions" className="space-y-4">
          <CompanyPositionsTab
            companyId={id || ''}
            companyName={company?.name || ''}
            positions={positions}
            loading={positionsLoading}
            error={positionsError}
            searchTerm={positionsSearchTerm}
            setSearchTerm={setPositionsSearchTerm}
            viewMode={positionsViewMode}
            setViewMode={setPositionsViewMode}
          />
        </TabsContent>
        
        <TabsContent value="documents" className="space-y-4">
          {id && <CompanyDocumentsTab companyId={id} />}
        </TabsContent>
        
        <TabsContent value="tasks" className="space-y-4">
          {id && <CompanyTasksTab companyId={id} companyName={company.name} />}
        </TabsContent>
        
        <TabsContent value="comments" className="space-y-4">
          {id && <CompanyComments companyId={id} />}
        </TabsContent>
        
        <TabsContent value="history" className="space-y-4">
          {id && <CompanyHistoryTab companyId={id} />}
        </TabsContent>
        
        <TabsContent value="mailbox" className="bg-card border border-border rounded-sm p-6">
          <EntityMailbox entityType="company" entityId={id || ''} entityName={company?.name} />
        </TabsContent>
        

      </Tabs>
      
      <AddContactForm
        isOpen={addContactModalOpen}
        onClose={() => setAddContactModalOpen(false)}
        onSubmit={handleAddContact}
        companyId={id || ''}
      />
    </div>
  );
};

export default CompanyDetail;
